/**
 * batch-59 loonstrook-controles (FO-433) — de PURE controle-regels.
 *
 * Twee vergelijkingen en één matcher, allemaal zonder DB of AI, zodat de
 * scherpste regels van dit FO direct unit-getest zijn:
 *
 *   1. `vergelijkVoertuigVermelding` - het kenteken op de strook tegen de
 *      berijder-historie van DIE periode (niet alleen "actueel"). Een
 *      voertuigwissel op de 15e mag geen valse afwijking opleveren (T3).
 *   2. `vergelijkBijtelling` - bijtelling op de strook tegen de registratie
 *      (`Employee.bijtelling`), in beide richtingen (T2).
 *   3. `zoekPassendeInhouding` - de inhoudingsregel die bij een openstaande
 *      boete past (bedrag ± tolerantie), oudste boete eerst (T4).
 *
 * Alles is "fail-soft naar NIET_VAST_TE_STELLEN": kon de extractie het veld niet
 * lezen, dan is dat een eigen uitkomst en NOOIT een afwijking (spec §6.5 / T9).
 *
 * AVG: hier komen geen BSN's langs. Bijtellingbedragen zijn wél gevoelig
 * salarisdetail - deze module rekent ermee maar logt niets.
 */

import type {
  LoonstrookControleAfwijkingType,
  LoonstrookInhouding,
} from '@streep/types'

// ─── Kenteken-normalisatie ───────────────────────────────────────────────────

// `normalizeKenteken` uit `voertuig-utils` is en blijft DE kenteken-helper
// (READ-ONLY hergebruik, ADDENDA "GEDEELDE BESTANDEN"); hier lokaal
// her-uitdrukken zou drift geven. Niet opnieuw re-exporteren: de barrel
// exporteert `voertuig-utils` al.
import { normalizeKenteken } from './voertuig-utils'

/**
 * Een NL-kenteken is 6 alfanumerieke tekens. De vermelding op de strook is
 * vrije tekst ("Auto van de zaak 39-PF-NJ", "Bijtelling bedrijfsauto V-65-PXH"),
 * dus we zoeken naar de eerste plausibele 6-teken-groep die niet volledig uit
 * cijfers of volledig uit letters bestaat.
 *
 * Waarom die extra eis: "Bijtelling 250000" of een los woord als "PRIVEGE"
 * levert anders een spookkenteken op, en een spookkenteken zou een valse
 * afwijking worden. Liever `null` (= niet vast te stellen) dan een verzonnen
 * match.
 */
export function leesKentekenUitVermelding(vermelding: string | null | undefined): string | null {
  if (!vermelding) return null
  // FO-444 (spec 144) — bredere normalisatie. NMBRS (en de extractie) schrijven
  // een kenteken ook als "v 65 pxh", "V.65.PXH" of "39 - PF - NJ"; de oude
  // token-splitsing op spaties zag die als drie losse stukjes en vond niets.
  // Daarom: eerst de strikte sidecode-vormen zoeken over aaneengesloten
  // tokens (1..3 tokens samengevoegd), daarna pas de oude lenige regel.
  const upper = vermelding.toUpperCase()

  // 1. Strikte NL-sidecodes op de "woorden" zoals ze er staan (streepjes en
  // punten binnen een woord horen bij het kenteken: "39-PF-NJ", "V.65.PXH").
  const woorden = upper.split(/[^A-Z0-9.-]+/).map((s) => normalizeKenteken(s))
  for (const k of woorden) {
    if (k.length === 6 && isNlSidecode(k)) return k
  }

  // 2. Strikte sidecodes over 2..3 opeenvolgende KORTE stukjes ("V 65 PXH",
  // "39 - PF - NJ"). Alleen stukjes van hooguit 3 tekens die elk puur cijfers
  // óf puur letters zijn - zo wordt "ZAAK" + "39" nooit een kenteken.
  const stukjes = upper.split(/[^A-Z0-9]+/).filter((t) => t.length > 0)
  const isGroep = (t: string): boolean => t.length <= 3 && (/^\d+$/.test(t) || /^[A-Z]+$/.test(t))
  for (let i = 0; i < stukjes.length; i++) {
    for (let n = 2; n <= 3 && i + n <= stukjes.length; n++) {
      const delen = stukjes.slice(i, i + n)
      if (!delen.every(isGroep)) continue
      const k = delen.join('')
      if (k.length === 6 && isNlSidecode(k)) return k
    }
  }

  // 3. Lenig (het bestaande gedrag): de eerste 6-teken-groep met letter én
  // cijfer. Nooit volledig cijfers/letters, en - nieuw - nooit met een klinker:
  // NL-kentekens dragen die niet, en "AUTO-07" (een NMBRS-voertuigcode) werd
  // anders als spookkenteken gelezen.
  const kandidaten = upper
    .split(/[^A-Z0-9-]+/)
    .map((s) => normalizeKenteken(s))
    .filter((s) => s.length === 6)
  for (const k of kandidaten) {
    const heeftLetter = /[A-Z]/.test(k)
    const heeftCijfer = /[0-9]/.test(k)
    const heeftKlinker = /[AEIOU]/.test(k)
    if (heeftLetter && heeftCijfer && !heeftKlinker) return k
  }
  return null
}

/**
 * Het kenteken van de auto van de zaak uit de TEKSTLAAG van een NMBRS-strook.
 *
 * NMBRS drukt het af als "Ja/VTF-02-K" in de kop, maar niet naast het label
 * "Auto v/d zaak:": het staat een regel lager, onder "Cat. waarde-%:", en naast
 * het label staat "Speciale tabel: 0". De AI-uitlezing nam daardoor geregeld
 * de verkeerde regel ("Auto v/d zaak: 0", "Fisc.bijtelling auto van de zaak")
 * en miste het kenteken bij ruim een derde van de stroken met een auto. In de
 * tekst is de "Ja/<kenteken>"-vorm eenduidig; deze regel leest hem zonder AI.
 *
 * Alleen een echte NL-sidecode telt (geen "Ja/Ja"). Noemt de tekst twee
 * VERSCHILLENDE kentekens, dan `null`: niet gokken welke auto bedoeld is.
 */
export function leesKentekenUitStrookTekst(
  tekst: string | null | undefined,
): { kenteken: string; vermelding: string } | null {
  if (!tekst) return null
  const gevonden = new Map<string, string>()
  const re = /\bJa\s*\/\s*([A-Z0-9]{1,3}\s*-\s*[A-Z0-9]{1,3}\s*-\s*[A-Z0-9]{1,3})\b/gi
  for (const m of tekst.matchAll(re)) {
    const ruw = (m[1] ?? '').replace(/\s+/g, '').toUpperCase()
    const k = normalizeKenteken(ruw)
    if (k.length === 6 && isNlSidecode(k) && !gevonden.has(k)) gevonden.set(k, ruw)
  }
  if (gevonden.size !== 1) return null
  const [[kenteken, ruw]] = [...gevonden.entries()] as [[string, string]]
  return { kenteken, vermelding: `Auto v/d zaak: Ja/${ruw}` }
}

/**
 * De NL-sidecodes (1..14) als letter/cijfer-patroon op het genormaliseerde
 * 6-teken-kenteken. Bron: RDW-sidecode-tabel. Klinkers komen op NL-kentekens
 * niet voor; die eis houdt woorden als "ZAAK39" of "AUTO07" buiten de deur.
 * Een treffer hier is vrijwel zeker een echt kenteken (geen spookmatch op een
 * woord of bedrag).
 */
const NL_SIDECODE_PATRONEN: readonly RegExp[] = [
  /^[A-Z]{2}\d{4}$/, // XX-99-99
  /^\d{4}[A-Z]{2}$/, // 99-99-XX
  /^\d{2}[A-Z]{2}\d{2}$/, // 99-XX-99
  /^[A-Z]{2}\d{2}[A-Z]{2}$/, // XX-99-XX
  /^[A-Z]{4}\d{2}$/, // XX-XX-99
  /^\d{2}[A-Z]{4}$/, // 99-XX-XX
  /^\d{2}[A-Z]{3}\d$/, // 99-XXX-9
  /^\d[A-Z]{3}\d{2}$/, // 9-XXX-99
  /^[A-Z]{2}\d{3}[A-Z]$/, // XX-999-X
  /^[A-Z]\d{3}[A-Z]{2}$/, // X-999-XX
  /^[A-Z]{3}\d{2}[A-Z]$/, // XXX-99-X
  /^[A-Z]\d{2}[A-Z]{3}$/, // X-99-XXX
  /^\d[A-Z]{2}\d{3}$/, // 9-XX-999
  /^\d{3}[A-Z]{2}\d$/, // 999-XX-9
]

/** Is dit genormaliseerde 6-teken-kenteken een geldige NL-sidecode-vorm? */
export function isNlSidecode(kentekenNormalized: string): boolean {
  if (/[AEIOU]/.test(kentekenNormalized)) return false
  return NL_SIDECODE_PATRONEN.some((re) => re.test(kentekenNormalized))
}

// ─── FO-444 (spec 144) — matcher-cascade zonder kenteken ─────────────────────

/**
 * Normaliseer een voertuigvermelding tot de sleutel van een leer-mapping:
 * lowercase, leestekens weg, whitespace samengevouwen. "Fisc.bijtelling auto
 * van de zaak" en "Fisc. bijtelling  Auto van de Zaak" worden zo dezelfde sleutel.
 * Cijfers blijven staan (een NMBRS-code als "AUTO-12" blijft onderscheidend).
 */
export function normaliseerVoertuigVermelding(vermelding: string | null | undefined): string {
  if (!vermelding) return ''
  return vermelding
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

export type VoertuigMatchSleutel =
  | 'KENTEKEN'
  | 'LEER_MAPPING'
  | 'NMBRS_REFERENTIE'
  | 'MERK_MODEL'
  | 'CATALOGUSWAARDE'
  | 'BIJTELLINGSBEDRAG'
  | 'ENIG_VOERTUIG'

/** Een voertuig dat in de periode aan de medewerker gekoppeld was (berijder-historie). */
export interface VoertuigKandidaat {
  voertuigId: string
  kenteken: string
  merk: string | null
  model: string | null
  cataloguswaarde: number | null
  /** `Voertuig.nmbrsReferentie` - de code die NMBRS op de strook afdrukt. */
  nmbrsReferentie: string | null
  /** Verwachte bruto bijtelling per maand voor dít voertuig (EUR), of null. */
  bijtellingMaandbedrag: number | null
}

/** Een ACTIEVE leer-mapping van deze medewerker. */
export interface VoertuigLeerMapping {
  id: string
  voertuigId: string
  vermeldingNormalized: string
}

export interface VoertuigMatchInput {
  /** De letterlijke vermelding op de strook. */
  vermelding: string | null
  /** Al geparsed kenteken (genormaliseerd) of null. */
  kenteken: string | null
  /** Merk/model zoals de extractie het apart teruggaf, of null. */
  merkModel: string | null
  /** Cataloguswaarde zoals op de strook, of null. */
  cataloguswaarde: number | null
  /** Bruto bijtelling per maand op de strook, of null. */
  bijtellingBruto: number | null
  bijtellingAanwezig: boolean | null
  kandidaten: readonly VoertuigKandidaat[]
  mappings: readonly VoertuigLeerMapping[]
  /** FO-361-registratie op de medewerker: gekoppeld bijtelling-voertuig + maandbedrag. */
  registratie: { voertuigId: string | null; maandbedrag: number | null } | null
}

export interface VoertuigMatch {
  sleutel: VoertuigMatchSleutel
  voertuigId: string
  kenteken: string
  mappingId: string | null
  toelichting: string
}

export type VoertuigMatchResultaat =
  | { status: 'MATCH'; match: VoertuigMatch; verouderdeMappingIds: string[] }
  /** Een sleutel wees meerdere kandidaten aan: nooit gokken (spec par. 6.2). */
  | {
      status: 'NIET_EENDUIDIG'
      sleutel: VoertuigMatchSleutel
      kandidaatIds: string[]
      toelichting: string
      verouderdeMappingIds: string[]
    }
  | { status: 'GEEN'; toelichting: string; verouderdeMappingIds: string[] }

/** Tolerantie op de cataloguswaarde: 1% met een bodem van EUR 50 (afronding op de strook). */
export function cataloguswaardeTolerantie(waarde: number): number {
  return Math.max(50, Math.abs(waarde) * 0.01)
}

/** Tolerantie op een bijtellingsbedrag per maand (EUR). */
export const BIJTELLING_BEDRAG_TOLERANTIE_EUR = 2

/**
 * Zoek uit welk gekoppeld voertuig de strook bedoelt, in de vaste cascade uit
 * spec 144 par. 3.A.2:
 *
 *   KENTEKEN -> LEER_MAPPING -> NMBRS_REFERENTIE -> MERK_MODEL ->
 *   CATALOGUSWAARDE -> BIJTELLINGSBEDRAG -> ENIG_VOERTUIG
 *
 * Elke stap levert alleen een treffer wanneer hij EENDUIDIG is; twee identieke
 * bussen op merk/model geven `NIET_EENDUIDIG` en daarmee handwerk (T2). Een
 * leer-mapping naar een voertuig dat niet (meer) in de periode gekoppeld is,
 * wordt NIET toegepast en komt terug in `verouderdeMappingIds` zodat de caller
 * hem op "te herbevestigen" zet (T4).
 *
 * `ENIG_VOERTUIG` is de laagste sport: de strook noemt een auto van de zaak
 * zonder identiteit, en STREEP kent in deze periode precies één gekoppeld
 * voertuig. Dat is consistent - een verkeerde auto is dan niet te detecteren,
 * maar er is ook geen aanwijzing dat er iets mis is. De sleutel maakt in de
 * lijst zichtbaar dat de identiteit niet op de strook stond.
 *
 * Puur: geen DB, geen AI. De caller levert de berijder-historie van DIE
 * periode (T3) en de actieve mappings.
 */
export function matchVoertuigVermelding(input: VoertuigMatchInput): VoertuigMatchResultaat {
  const verouderd: string[] = []
  const kandidaten = input.kandidaten
  const heeftVermelding = (input.vermelding ?? '').trim().length > 0
  const kentekenById = new Map(kandidaten.map((k) => [k.voertuigId, normalizeKenteken(k.kenteken)]))
  const maak = (
    sleutel: VoertuigMatchSleutel,
    k: VoertuigKandidaat,
    toelichting: string,
    mappingId: string | null = null,
  ): VoertuigMatchResultaat => ({
    status: 'MATCH',
    match: { sleutel, voertuigId: k.voertuigId, kenteken: kentekenById.get(k.voertuigId) ?? '', mappingId, toelichting },
    verouderdeMappingIds: verouderd,
  })

  if (kandidaten.length === 0) {
    return { status: 'GEEN', toelichting: 'Geen voertuig gekoppeld in deze periode.', verouderdeMappingIds: verouderd }
  }

  // 1. KENTEKEN
  const kenteken =
    input.kenteken != null && input.kenteken !== ''
      ? normalizeKenteken(input.kenteken)
      : leesKentekenUitVermelding(input.vermelding)
  if (kenteken) {
    const treffer = kandidaten.find((k) => kentekenById.get(k.voertuigId) === kenteken)
    if (treffer) return maak('KENTEKEN', treffer, `Kenteken ${kenteken} op de strook.`)
    // Een leesbaar kenteken dat NIET in de set zit is een echte afwijking; de
    // cascade gaat daar niet overheen gokken.
    return {
      status: 'GEEN',
      toelichting: `Kenteken ${kenteken} op de strook hoort niet bij een gekoppeld voertuig.`,
      verouderdeMappingIds: verouderd,
    }
  }

  if (!heeftVermelding) {
    return { status: 'GEEN', toelichting: 'Geen voertuigvermelding op de strook.', verouderdeMappingIds: verouderd }
  }

  // 2. LEER_MAPPING
  const sleutel = normaliseerVoertuigVermelding(input.vermelding)
  for (const m of input.mappings) {
    if (m.vermeldingNormalized !== sleutel) continue
    const treffer = kandidaten.find((k) => k.voertuigId === m.voertuigId)
    if (treffer) {
      return maak('LEER_MAPPING', treffer, 'Eerder handmatig gekoppeld (leer-mapping).', m.id)
    }
    verouderd.push(m.id)
  }

  // 3. NMBRS_REFERENTIE - de code van het voertuig komt als los woord in de vermelding voor.
  const woorden = new Set(sleutel.split(' '))
  const opReferentie = kandidaten.filter((k) => {
    const ref = normaliseerVoertuigVermelding(k.nmbrsReferentie)
    if (!ref) return false
    return ref.split(' ').every((w) => woorden.has(w)) || sleutel.includes(ref)
  })
  if (opReferentie.length === 1) {
    return maak('NMBRS_REFERENTIE', opReferentie[0], `NMBRS-referentie "${opReferentie[0].nmbrsReferentie}" op de strook.`)
  }
  if (opReferentie.length > 1) {
    return nietEenduidig('NMBRS_REFERENTIE', opReferentie, 'Meerdere gekoppelde voertuigen dragen deze NMBRS-referentie.', verouderd)
  }

  // 4. MERK_MODEL - merk (en liefst model) van de kandidaat staat in de tekst.
  const tekst = normaliseerVoertuigVermelding(`${input.merkModel ?? ''} ${input.vermelding ?? ''}`)
  const opMerk = kandidaten
    .map((k) => {
      const merk = normaliseerVoertuigVermelding(k.merk)
      const model = normaliseerVoertuigVermelding(k.model)
      if (!merk || !bevatWoorden(tekst, merk)) return null
      const metModel = model.length > 0 && bevatWoorden(tekst, model)
      return { k, score: metModel ? 2 : 1 }
    })
    .filter((x): x is { k: VoertuigKandidaat; score: number } => x !== null)
  if (opMerk.length > 0) {
    const beste = Math.max(...opMerk.map((x) => x.score))
    const top = opMerk.filter((x) => x.score === beste)
    if (top.length === 1) {
      const k = top[0].k
      return maak('MERK_MODEL', k, `Merk/model "${[k.merk, k.model].filter(Boolean).join(' ')}" op de strook.`)
    }
    return nietEenduidig('MERK_MODEL', top.map((x) => x.k), 'Meerdere gekoppelde voertuigen met dit merk/model; kies handmatig.', verouderd)
  }

  // 5. CATALOGUSWAARDE
  if (input.cataloguswaarde != null && Number.isFinite(input.cataloguswaarde) && input.cataloguswaarde > 0) {
    const tol = cataloguswaardeTolerantie(input.cataloguswaarde)
    const opWaarde = kandidaten.filter(
      (k) => k.cataloguswaarde != null && Math.abs(k.cataloguswaarde - (input.cataloguswaarde as number)) <= tol,
    )
    if (opWaarde.length === 1) {
      return maak('CATALOGUSWAARDE', opWaarde[0], `Cataloguswaarde op de strook komt overeen (tolerantie EUR ${Math.round(tol)}).`)
    }
    if (opWaarde.length > 1) {
      return nietEenduidig('CATALOGUSWAARDE', opWaarde, 'Meerdere gekoppelde voertuigen met deze cataloguswaarde.', verouderd)
    }
  }

  // 6. BIJTELLINGSBEDRAG - het maandbedrag op de strook tegen de verwachte
  // bijtelling per voertuig, of tegen de FO-361-registratie op de medewerker.
  if (input.bijtellingBruto != null && Number.isFinite(input.bijtellingBruto) && input.bijtellingBruto > 0) {
    const bedrag = input.bijtellingBruto
    const opBedrag = kandidaten.filter(
      (k) => k.bijtellingMaandbedrag != null && Math.abs(k.bijtellingMaandbedrag - bedrag) <= BIJTELLING_BEDRAG_TOLERANTIE_EUR,
    )
    if (opBedrag.length === 1) {
      return maak('BIJTELLINGSBEDRAG', opBedrag[0], 'Bijtellingsbedrag op de strook past bij de bijtelling van dit voertuig.')
    }
    if (opBedrag.length > 1) {
      return nietEenduidig('BIJTELLINGSBEDRAG', opBedrag, 'Meerdere gekoppelde voertuigen met dit bijtellingsbedrag.', verouderd)
    }
    const reg = input.registratie
    if (
      reg?.voertuigId &&
      reg.maandbedrag != null &&
      Math.abs(reg.maandbedrag - bedrag) <= BIJTELLING_BEDRAG_TOLERANTIE_EUR
    ) {
      const treffer = kandidaten.find((k) => k.voertuigId === reg.voertuigId)
      if (treffer) {
        return maak('BIJTELLINGSBEDRAG', treffer, 'Bijtellingsbedrag op de strook past bij de bijtelling-registratie van de medewerker.')
      }
    }
  }

  // 7. ENIG_VOERTUIG
  if (kandidaten.length === 1) {
    return maak(
      'ENIG_VOERTUIG',
      kandidaten[0],
      'De strook noemt een auto van de zaak zonder identiteit; STREEP koppelt in deze periode precies één voertuig.',
    )
  }

  return {
    status: 'NIET_EENDUIDIG',
    sleutel: 'ENIG_VOERTUIG',
    kandidaatIds: kandidaten.map((k) => k.voertuigId),
    toelichting: 'De strook noemt een auto van de zaak zonder identiteit en STREEP koppelt meerdere voertuigen in deze periode; koppel handmatig.',
    verouderdeMappingIds: verouderd,
  }
}

function nietEenduidig(
  sleutel: VoertuigMatchSleutel,
  kandidaten: readonly VoertuigKandidaat[],
  toelichting: string,
  verouderdeMappingIds: string[],
): VoertuigMatchResultaat {
  return {
    status: 'NIET_EENDUIDIG',
    sleutel,
    kandidaatIds: kandidaten.map((k) => k.voertuigId),
    toelichting,
    verouderdeMappingIds,
  }
}

/** Staat elk woord van `naald` als los woord in `tekst` (beide genormaliseerd)? */
function bevatWoorden(tekst: string, naald: string): boolean {
  const woorden = new Set(tekst.split(' '))
  return naald.split(' ').every((w) => w.length > 0 && woorden.has(w))
}

// ─── 1. Voertuig-vergelijking ────────────────────────────────────────────────

export interface VoertuigVergelijkInput {
  /** De letterlijke tekst op de strook; null = niets gelezen. */
  strookVermelding: string | null
  /**
   * Genormaliseerd kenteken uit de vermelding (mag door de caller vooraf
   * bepaald zijn); null = de caller laat deze module het zelf lezen.
   */
  strookKenteken?: string | null
  /**
   * De kentekens die de app in DEZE periode aan de medewerker koppelde
   * (berijder-historie, niet alleen de actuele toewijzing). Leeg = geen
   * gekoppeld voertuig in die periode.
   */
  periodeKentekens: readonly string[]
  /**
   * Kon de extractie het voertuigveld überhaupt beoordelen? `false` = de strook
   * is niet uitgelezen (geen AI-inzage / extractie mislukt) -> nooit een
   * afwijking, altijd "niet vast te stellen".
   */
  strookGelezen: boolean
  /**
   * Mag je op DEZE strook een voertuigvermelding verwachten?
   *
   * In de praktijk noemt een loonstrook alleen een auto wanneer er een fiscale
   * bijtellingregel op staat. Een bedrijfsbus zonder privégebruik staat wél in
   * STREEP gekoppeld maar komt nooit op de strook - dat is normaal en geen fout.
   * Zonder deze vlag zou elke bus-berijder ELKE run een afwijking opleveren
   * (in de huidige registratie tientallen per run), en verdrinkt het echte
   * signaal. `false` onderdrukt daarom uitsluitend
   * `VOERTUIG_GEKOPPELD_NIET_OP_STROOK`; een AFWIJKEND kenteken op de strook
   * blijft altijd een afwijking, ook zonder bijtelling.
   */
  verwachtVermelding: boolean
  /**
   * FO-444 (spec 144) — het voertuig dat de matcher-cascade
   * (`matchVoertuigVermelding`) uit een vermelding ZONDER kenteken herkende.
   * Wanneer gezet, geldt dat als het voertuig op de strook: een vermelding als
   * "Fisc.bijtelling auto van de zaak" is dan niet langer "niet vast te stellen"
   * maar vergeleken tegen de berijder-historie. Null/undefined = geen match.
   */
  strookVoertuigMatch?: { voertuigId: string; kenteken: string; sleutel: VoertuigMatchSleutel } | null
}

export interface VoertuigVergelijkUitkomst {
  type: LoonstrookControleAfwijkingType
  strookKenteken: string | null
  /** Het app-kenteken waartegen we het verschil uitleggen (eerste uit de set). */
  appKenteken: string | null
  toelichting: string
}

/**
 * Vergelijk het voertuig op de strook met de berijder-historie van de periode.
 * Returnt `null` wanneer alles klopt.
 *
 * De historie-set is de kern van T3: zolang het kenteken op de strook in de
 * periode aan deze medewerker gekoppeld wás, is er geen afwijking - ook niet als
 * hij inmiddels in een andere auto rijdt.
 */
export function vergelijkVoertuigVermelding(
  input: VoertuigVergelijkInput,
): VoertuigVergelijkUitkomst | null {
  const appKentekens = input.periodeKentekens
    .map((k) => normalizeKenteken(k))
    .filter((k) => k.length > 0)
  const appKenteken = appKentekens[0] ?? null
  const appLijst = appKentekens.join(', ')

  if (!input.strookGelezen) {
    return {
      type: 'NIET_VAST_TE_STELLEN',
      strookKenteken: null,
      appKenteken,
      toelichting: 'De strook is niet uitgelezen; het voertuigveld is niet vast te stellen.',
    }
  }

  const gelezenKenteken =
    input.strookKenteken != null && input.strookKenteken !== ''
      ? normalizeKenteken(input.strookKenteken)
      : leesKentekenUitVermelding(input.strookVermelding)

  const heeftVermelding = (input.strookVermelding ?? '').trim().length > 0

  // FO-444 — geen kenteken op de strook, maar de cascade herkende het voertuig
  // wél (leer-mapping, NMBRS-referentie, merk/model, cataloguswaarde,
  // bijtellingsbedrag of het enige gekoppelde voertuig): dat kenteken telt.
  const strookKenteken =
    gelezenKenteken ??
    (heeftVermelding && input.strookVoertuigMatch
      ? normalizeKenteken(input.strookVoertuigMatch.kenteken) || null
      : null)

  // Vermelding aanwezig, maar er zit geen leesbaar kenteken in: niet vast te
  // stellen (spec §6.5) - beslist geen afwijking.
  if (heeftVermelding && !strookKenteken) {
    return {
      type: 'NIET_VAST_TE_STELLEN',
      strookKenteken: null,
      appKenteken,
      toelichting:
        'Op de strook staat een voertuigvermelding zonder herkenbaar kenteken; niet vast te stellen.',
    }
  }

  if (!strookKenteken && appKentekens.length === 0) return null

  if (!strookKenteken) {
    // Geen bijtelling verwacht -> een ontbrekende vermelding is normaal (een
    // bedrijfsbus zonder privégebruik staat niet op de loonstrook).
    if (!input.verwachtVermelding) return null
    return {
      type: 'VOERTUIG_GEKOPPELD_NIET_OP_STROOK',
      strookKenteken: null,
      appKenteken,
      toelichting: `De app koppelt ${appLijst} in deze periode met bijtelling, maar op de strook staat geen voertuig.`,
    }
  }

  if (appKentekens.length === 0) {
    return {
      type: 'VOERTUIG_OP_STROOK_ZONDER_KOPPELING',
      strookKenteken,
      appKenteken: null,
      toelichting: `De strook noemt ${strookKenteken}, maar in deze periode is er geen voertuig gekoppeld.`,
    }
  }

  if (appKentekens.includes(strookKenteken)) return null

  return {
    type: 'VOERTUIG_KENTEKEN_AFWIJKING',
    strookKenteken,
    appKenteken,
    toelichting: `De strook noemt ${strookKenteken}; de app koppelt ${appLijst} in deze periode.`,
  }
}

// ─── 2. Bijtelling-vergelijking ──────────────────────────────────────────────

/**
 * Wordt er op de strook bijtelling in rekening gebracht? TRI-STATE, zoals
 * `Loonstrook.bijtellingAanwezig`.
 *
 * Alleen een BEDRAG telt. NMBRS zet bij iedere berijder van een bedrijfsvoertuig
 * "Auto v/d zaak: Ja/<kenteken>" op de strook, ook bij een bus zonder
 * privegebruik; de uitlezing las dat als "bijtelling aanwezig" en elke
 * busberijder werd een afwijking. Staat er een vermelding maar geen bedrag, dan
 * wordt er geen bijtelling gerekend.
 *
 * `null` blijft `null` (niet gelezen = niet vast te stellen), tenzij er tóch een
 * bedrag gelezen is.
 */
export function bijtellingInRekening(input: {
  aanwezig: boolean | null
  bruto: number | null
  /** Bedragen van BIJTELLING-componenten op de strook (mag ontbreken). */
  componentBedragen?: readonly (number | null)[]
}): boolean | null {
  const heeftBedrag = (n: number | null | undefined): boolean =>
    n != null && Number.isFinite(n) && n !== 0
  if (heeftBedrag(input.bruto)) return true
  if ((input.componentBedragen ?? []).some(heeftBedrag)) return true
  if (input.aanwezig == null) return null
  return false
}

export interface BijtellingVergelijkInput {
  /**
   * Stond er bijtelling op de strook? TRI-STATE: `null` = de extractie kon het
   * niet vaststellen (nooit een afwijking).
   */
  strookAanwezig: boolean | null
  /** Bedrag van de bijtelling op de strook (EUR/maand), of null. */
  strookBedrag: number | null
  /** `Employee.bijtelling`; null = niet geregistreerd (= geen bijtelling). */
  registratieAan: boolean | null
  /** `Employee.bijtellingMaandbedrag` (EUR/maand), of null. */
  registratieBedrag: number | null
}

export interface BijtellingVergelijkUitkomst {
  type: LoonstrookControleAfwijkingType
  toelichting: string
}

/**
 * Vergelijk de AANWEZIGHEID van bijtelling op de strook met de registratie.
 *
 * Bewust alleen aanwezigheid, geen bedrag: het BEDRAG wordt al adviserend
 * gecontroleerd door de bestaande cross-check (`bijtelling-crosscheck.ts`,
 * spec 40 fase 5). Die vuurt uitsluitend wanneer BEIDE waarden bestaan en zag
 * daardoor precies de twee gevallen niet die dit FO vraagt: bijtelling op de
 * strook zonder registratie, en registratie zonder bijtelling op de strook.
 * Twee vergelijkingen, geen overlap, dus ook geen dubbele melding.
 */
export function vergelijkBijtelling(
  input: BijtellingVergelijkInput,
): BijtellingVergelijkUitkomst | null {
  if (input.strookAanwezig == null) {
    return {
      type: 'NIET_VAST_TE_STELLEN',
      toelichting: 'De bijtelling op de strook is niet vast te stellen (veld niet gelezen).',
    }
  }
  const registratie = input.registratieAan === true
  if (input.strookAanwezig && !registratie) {
    return {
      type: 'BIJTELLING_OP_STROOK_ZONDER_REGISTRATIE',
      toelichting:
        'De strook toont bijtelling, maar in de app staat geen bijtelling geregistreerd.',
    }
  }
  if (!input.strookAanwezig && registratie) {
    return {
      type: 'BIJTELLING_GEREGISTREERD_NIET_OP_STROOK',
      toelichting:
        'In de app staat bijtelling geregistreerd, maar op de strook staat er geen.',
    }
  }
  return null
}

// ─── 3. Boete-matcher ────────────────────────────────────────────────────────

/** Standaard-tolerantie (EUR) waarbinnen een inhouding bij een boete past. */
export const BOETE_INHOUDING_TOLERANTIE_EUR = 1

/** Woorden die een inhoudingsregel als boete-inhouding herkenbaar maken. */
const BOETE_TREFWOORDEN = ['boete', 'bekeuring', 'cjib', 'verkeersovertreding', 'naheffing parkeren']

/** Is deze omschrijving expliciet een boete-inhouding? */
export function isBoeteOmschrijving(omschrijving: string | null | undefined): boolean {
  const s = (omschrijving ?? '').toLowerCase()
  return BOETE_TREFWOORDEN.some((w) => s.includes(w))
}

export interface InhoudingKandidaat extends LoonstrookInhouding {
  /** Index in de originele array; de caller markeert 'm daarmee als verbruikt. */
  index: number
}

/**
 * Zet de inhoudingsregels van een strook om naar matchbare kandidaten.
 *
 * `WETTELIJK` valt af: loonheffing/pensioen/WIA staan op élke strook en zouden
 * anders toevallig op een boetebedrag kunnen uitkomen. Bedragen worden als
 * absolute waarde genomen (een inhouding kan als negatief bedrag op de strook
 * staan) en regels zonder bedrag vallen af.
 */
export function inhoudingKandidaten(
  inhoudingen: readonly LoonstrookInhouding[],
): InhoudingKandidaat[] {
  const out: InhoudingKandidaat[] = []
  inhoudingen.forEach((r, index) => {
    if (r.categorie === 'WETTELIJK') return
    const bedrag = Math.abs(Number(r.bedrag))
    if (!Number.isFinite(bedrag) || bedrag <= 0) return
    out.push({ ...r, bedrag, index })
  })
  return out
}

/**
 * Zoek de inhoudingsregel die bij een openstaand boetebedrag past.
 *
 * Volgorde van voorkeur - deterministisch, zodat dezelfde strook altijd
 * dezelfde koppeling oplevert:
 *   1. binnen tolerantie én herkenbaar een boete-regel ("Inhouding boete");
 *   2. ALLEEN zonder `alleenBoeteRegels`: binnen tolerantie op bedrag alleen;
 *   3. ALLEEN met `toestaanDeelbedrag`: een boete-regel met een bedrag KLEINER
 *      dan het openstaande bedrag - de termijnbetaling uit §6.3.
 * Bij gelijke score wint de kleinste bedrag-afwijking, daarna de laagste index.
 *
 * Waarom (2) voor de AUTOMATISCHE koppeling uit staat (`alleenBoeteRegels`):
 * een bedrag alleen bewijst niets. Een regel "Kledingbijdrage EUR 139" zou zich
 * anders vastklikken aan een boete van EUR 139, de boete op VERREKEND zetten en
 * daarmee ook het `verhaald`-spoor op de medewerker - een boeking die niemand
 * heeft gedaan. Zo'n bedrag-alleen-treffer is hooguit een aanwijzing; de
 * beheerder koppelt hem desgewenst zelf via de expliciete `/koppelen`-actie.
 *
 * Waarom (3) standaard UIT staat: een willekeurige inhouding die toevallig
 * kleiner is dan de boete zou de boete anders stil op "deels verrekend" zetten
 * en het restbedrag verkeerd berekenen. De service zet de vlag alleen aan
 * wanneer er precies ÉÉN openstaande boete voor deze medewerker op deze run is.
 *
 * `verbruikt` bevat de indices die een eerdere (oudere) boete al claimde: twee
 * boetes van hetzelfde bedrag pakken zo nooit dezelfde regel (§6.2).
 */
export function zoekPassendeInhouding(
  openBedrag: number,
  kandidaten: readonly InhoudingKandidaat[],
  opties: {
    tolerantie?: number
    verbruikt?: ReadonlySet<number>
    toestaanDeelbedrag?: boolean
    /** Alleen regels die herkenbaar een boete zijn (de automatische koppeling). */
    alleenBoeteRegels?: boolean
  } = {},
): InhoudingKandidaat | null {
  const tolerantie = opties.tolerantie ?? BOETE_INHOUDING_TOLERANTIE_EUR
  const verbruikt = opties.verbruikt ?? new Set<number>()
  if (!Number.isFinite(openBedrag) || openBedrag <= 0) return null

  let beste: { kandidaat: InhoudingKandidaat; score: number; verschil: number } | null = null
  for (const k of kandidaten) {
    if (verbruikt.has(k.index)) continue
    const verschil = Math.abs(k.bedrag - openBedrag)
    const isBoete = k.categorie === 'BOETE' || isBoeteOmschrijving(k.omschrijving)
    let score: number | null = null
    if (verschil <= tolerantie && isBoete) score = 3
    else if (verschil <= tolerantie && !opties.alleenBoeteRegels) score = 2
    else if (opties.toestaanDeelbedrag && isBoete && k.bedrag < openBedrag) score = 1
    if (score === null) continue
    if (
      beste === null ||
      score > beste.score ||
      (score === beste.score && verschil < beste.verschil) ||
      (score === beste.score && verschil === beste.verschil && k.index < beste.kandidaat.index)
    ) {
      beste = { kandidaat: k, score, verschil }
    }
  }
  return beste?.kandidaat ?? null
}

// ─── Run-sleutel ─────────────────────────────────────────────────────────────

export interface LoonstrookRunSleutelInput {
  jaar: number
  maand: number | null
  periodeNummer: number | null
  soort: 'LOONSTROOK' | 'JAAROPGAVE'
  runNummer: number
}

/**
 * Deterministische sleutel van één verloningsrun: "2026-06:LOONSTROOK:run1".
 *
 * Dit is de idempotentiegrens van de hele controle-stap: een herdraai van
 * dezelfde import werkt hetzelfde rapport bij i.p.v. een tweede aan te maken, en
 * de boete-bewaking meldt per run precies één keer.
 */
export function loonstrookRunSleutel(input: LoonstrookRunSleutelInput): string {
  const run = Number.isFinite(input.runNummer) ? Math.max(1, Math.trunc(input.runNummer)) : 1
  const periode =
    input.soort === 'JAAROPGAVE'
      ? String(input.jaar)
      : input.periodeNummer != null
        ? `${input.jaar}-p${String(input.periodeNummer).padStart(2, '0')}`
        : input.maand != null
          ? `${input.jaar}-${String(input.maand).padStart(2, '0')}`
          : String(input.jaar)
  return `${periode}:${input.soort}:run${run}`
}

/**
 * Het BEGIN van de verloningsperiode als UTC-instant (inclusief). Samen met
 * `loonstrookPeriodeEinde` het venster waarbinnen de berijder-historie gelezen
 * wordt: elke `VoertuigToewijzing` die dat venster overlapt telt mee, zodat een
 * voertuigwissel midden in de periode geen valse afwijking geeft (T3).
 */
export function loonstrookPeriodeStart(input: {
  jaar: number
  maand: number | null
  periodeNummer: number | null
  soort: 'LOONSTROOK' | 'JAAROPGAVE'
}): Date {
  if (input.soort === 'JAAROPGAVE' || (input.maand == null && input.periodeNummer == null)) {
    return new Date(Date.UTC(input.jaar, 0, 1))
  }
  if (input.periodeNummer != null) {
    return new Date(Date.UTC(input.jaar, 0, 1) + (input.periodeNummer - 1) * 28 * 86_400_000)
  }
  return new Date(Date.UTC(input.jaar, (input.maand ?? 1) - 1, 1))
}

/**
 * Het einde van de verloningsperiode als UTC-instant (exclusief), zodat je kunt
 * bepalen of een boete-datum nog in of vóór deze run valt.
 *
 * MONTH -> de eerste van de volgende maand; FOUR_WEEK -> 28 dagen na de start
 * van periode 1 (ISO-maandag-benadering is hier niet nodig: de bewaking vraagt
 * alleen "valt de boetedatum vóór het einde van deze run"). Jaaropgave -> 1
 * januari van het volgende jaar.
 */
export function loonstrookPeriodeEinde(input: {
  jaar: number
  maand: number | null
  periodeNummer: number | null
  soort: 'LOONSTROOK' | 'JAAROPGAVE'
}): Date {
  if (input.soort === 'JAAROPGAVE' || (input.maand == null && input.periodeNummer == null)) {
    return new Date(Date.UTC(input.jaar + 1, 0, 1))
  }
  if (input.periodeNummer != null) {
    const start = Date.UTC(input.jaar, 0, 1) + (input.periodeNummer - 1) * 28 * 86_400_000
    return new Date(start + 28 * 86_400_000)
  }
  const m = input.maand ?? 12
  return new Date(Date.UTC(input.jaar, m, 1))
}

/**
 * Hoort deze boete op DEZE run bewaakt te worden?
 *
 * Ja zodra de overtredingsdatum vóór het einde van de verloningsperiode ligt:
 * dan had de salarisverwerker hem in deze run kunnen inhouden. Een boete van ná
 * de periode blijft buiten beeld tot de eerstvolgende run (geen valse melding).
 */
export function boeteValtInRun(boeteDatum: Date, periodeEinde: Date): boolean {
  return boeteDatum.getTime() < periodeEinde.getTime()
}

/**
 * Ouderdom in hele dagen tussen twee DAG-instants (nooit negatief).
 *
 * `vandaagDagUtc` moet de NL-kalenderdag als UTC-middernacht zijn (bv. via
 * `nlDagUtcMidnight` aan de API-rand) - niet `new Date()`. Zo blijft deze
 * functie puur en DST-veilig: tussen 00:00 en 02:00 NL-tijd is de UTC-dag nog de
 * vorige, en zou de ouderdom er een dag naast zitten.
 */
export function dagenSinds(datum: Date, vandaagDagUtc: Date): number {
  const d = Date.UTC(datum.getUTCFullYear(), datum.getUTCMonth(), datum.getUTCDate())
  const n = Date.UTC(
    vandaagDagUtc.getUTCFullYear(),
    vandaagDagUtc.getUTCMonth(),
    vandaagDagUtc.getUTCDate(),
  )
  return Math.max(0, Math.round((n - d) / 86_400_000))
}
